import {
  YOTPO_LIST_TAG_NAME
} from "./chunk.3GRN2CUK.js";
import {
  fromEvent
} from "./chunk.DLI4WUT3.js";
import {
  OverlayComponent,
  overlayStyles
} from "./chunk.PXGMVSIO.js";
import {
  emit
} from "./chunk.HM4ZRC4T.js";
import {
  __decorateClass,
  component_styles_default,
  theme_styles_default
} from "./chunk.U4VUMZVF.js";

// src/components/popover/popover.ts
import { html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

// src/components/popover/popover.styles.ts
import { css } from "lit";
var popover_styles_default = css`
	${theme_styles_default}
	${component_styles_default}
	${overlayStyles}

	:host {
		--height: auto;
		--width: auto;
	}

	.popover {
		height: var(--height);
		width: var(--width);
		background: var(--yotpo-color-white);
		border-radius: var(--yotpo-border-radius-small);
		box-shadow: var(--yotpo-shadow-30);
		display: flex;
		flex-direction: column;
		flex-wrap: nowrap;
	}

	.popover__margins {
		padding: var(--yotpo-spacing-medium) 0;
	}

	.popover__header {
		flex-shrink: 0;
	}

	.popover__body {
		flex-grow: 1;
		overflow: auto;
		scroll-behavior: smooth;
	}

	.popover__body_overflowable {
		overflow: visible;
	}

	.popover__footer {
		flex-shrink: 0;
	}

	.popover__body::-webkit-scrollbar {
		width: 18px; /* 1.125rem */
	}

	.popover__body::-webkit-scrollbar-track {
		background: transparent;
	}

	.popover__body::-webkit-scrollbar-thumb {
		border: 5px solid rgba(0, 0, 0, 0); /* 0.313rem */
		background-clip: padding-box;
		background-color: var(--yotpo-color-brand-40);
		border-radius: 100px; /* 6.25rem */
		width: 8px; /* 0.5rem */
		outline: none;
	}

	.popover__body::-webkit-scrollbar-thumb:hover {
		background-color: var(--yotpo-color-brand-70);
		cursor: pointer;
	}
`;

// src/components/popover/popover.ts
var YotpoPopover = class extends OverlayComponent {
  constructor() {
    super();
    this.reachedScrollEnd = false;
    this.customEvents = ["yotpo-show" /* show */, "yotpo-hide" /* hide */, "yotpo-scroll-end" /* scrollEnd */];
    this.margins = true;
    this.overflowable = false;
    this.infiniteScrollable = false;
    this.scrollEndOffset = 0;
    this.placement = "bottom" /* bottom */ + "-" + "start" /* start */;
    this.distance = 0;
    this.possibleSlots = ["header", "footer", "body"];
  }
  async firstUpdated() {
    super.firstUpdated();
    this.setAttribute("role", "popover");
    await this.updateComplete;
    if (this.infiniteScrollable && this.scrollableElement) {
      const scrollableElementScroll = fromEvent(this.scrollableElement, "scroll");
      this.scrollSubscription = scrollableElementScroll.subscribe(this.handleScroll.bind(this));
    } else if (!this.scrollableElement) {
      console.error("Scrollable component should contain a scrollable element with `yotpo-scrollable-element` attribute!", this);
    }
  }
  disconnectedCallback() {
    var _a;
    super.disconnectedCallback();
    (_a = this.scrollSubscription) == null ? void 0 : _a.unsubscribe();
  }
  handleSlotChange() {
    this.updateOverlayContent();
  }
  stopEventPropagation(event) {
    event.stopPropagation();
  }
  handleScroll() {
    if (!this.scrollableElement) {
      return;
    }
    const isScrollOverEndOffset = this.scrollableElement.offsetHeight + this.scrollableElement.scrollTop + this.scrollEndOffset >= this.scrollableElement.scrollHeight;
    if (isScrollOverEndOffset && !this.reachedScrollEnd) {
      this.reachedScrollEnd = true;
      emit(this, "yotpo-scroll-end" /* scrollEnd */);
    } else if (!isScrollOverEndOffset) {
      this.reachedScrollEnd = false;
    }
  }
  scrollToTop() {
    if (!this.scrollableElement) {
      return;
    }
    this.scrollableElement.scrollTop = 0;
  }
  handleContentHide(event) {
    var _a;
    event.stopPropagation();
    const listHidingDropdown = event.detail;
    if (((_a = listHidingDropdown == null ? void 0 : listHidingDropdown.tagName) == null ? void 0 : _a.toLowerCase()) === YOTPO_LIST_TAG_NAME) {
      emit(this, "yotpo-hide" /* hide */, { detail: event.detail });
    }
  }
  render() {
    return this.renderOverlay(html`
			<div
				part="base"
				class=${classMap({
      popover: true,
      popover__margins: this.margins
    })}
				role="popover"
				@click=${this.stopEventPropagation}
				@keydown=${this.stopEventPropagation}
				@mousedown=${this.stopEventPropagation}
			>
				<span part="header" class="popover__header">
					<slot @slotchange=${this.handleSlotChange} name="header"></slot>
				</span>
				<span
					part="body"
					yotpo-scrollable-element
					class=${classMap({
      popover__body: true,
      popover__body_overflowable: this.overflowable
    })}
					@yotpo-hide=${this.handleContentHide}
				>
					<slot @slotchange=${this.handleSlotChange} name="body"> </slot>
				</span>
				<span part="footer" class="popover__footer">
					<slot @slotchange=${this.handleSlotChange} name="footer"></slot>
				</span>
			</div>
		`);
  }
};
YotpoPopover.styles = popover_styles_default;
__decorateClass([
  query(".popover")
], YotpoPopover.prototype, "popover", 2);
__decorateClass([
  query('[name="body"]')
], YotpoPopover.prototype, "bodySlot", 2);
__decorateClass([
  query("[yotpo-scrollable-element]")
], YotpoPopover.prototype, "scrollableElement", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], YotpoPopover.prototype, "margins", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], YotpoPopover.prototype, "overflowable", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], YotpoPopover.prototype, "infiniteScrollable", 2);
__decorateClass([
  property({ type: Number, reflect: true })
], YotpoPopover.prototype, "scrollEndOffset", 2);
YotpoPopover = __decorateClass([
  customElement("yotpo-popover")
], YotpoPopover);

export {
  YotpoPopover
};
